import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { TachographLoadUnloadModel } from '@/app/models/tachographLoadUnloadModel'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { CountryModel } from '@/app/models/timesheet/countryModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TmsRouteType } from '@/app/constants/tmsRouteType'
import { determineRouteType } from '@/app/common/tmsRouteTypeHelper'
import { translateCountryName } from '@/app/common/countryNames'
import { getTmsRouteTypeName } from '@/app/services/tms/tmsRouteTypeNameResolver'
import { getEventTypeIcon } from '@/app/common/converters/newDelegation/tripEventTypeToIconConverter'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import TmsDateTimeInput from '@/modules/drivers/shared/tms/TmsDateTimeInput'
import { getRowBackground } from '@/modules/drivers/shared/tms/TmsLoadsEditor'
import {
  TmsSourceFilter,
  useTmsCountryOptions,
} from '@/modules/drivers/shared/tms/TmsSourceFilter'
import { TmsSourceMode } from '@/modules/drivers/shared/tms/useTmsLoadsEditor'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { Dropdown } from '@/ui/form/controls/Dropdown'
import { Input } from '@/ui/form/primitives/Input'
import { Button } from '@/ui/button'
import { Loader } from '@/ui/feedback'
import { toast } from '@/lib/toasts/api'
import { FaIcon, faTrash } from '@/lib/fa'
import { TmsImportHeader } from './TmsImportHeader'

interface CardLoadsViewProps {
  companyId: string
  driverId: number
  driverName?: string
  dateFrom: Moment
  dateTo: Moment
  existingEvents: EventModel[]
  onClose: () => void
  onSave: (events: EventModel[]) => void
}

interface RawEntry extends TachographLoadUnloadModel {
  key: string
  disabled: boolean
  checked?: boolean
}

interface JoinedEntry {
  key: string
  docNumber: string
  loadingDate: Moment
  loadingCountry?: CountryModel
  unloadingDate: Moment
  unloadingCountry?: CountryModel
  vehicle?: TachographLoadUnloadModel['vehicle']
  // WPF TmsData.Loading: czy drugim klikniętym rekordem był załadunek; od tego zależy typ przewozu.
  loading: boolean
  routeType: TmsRouteType
}

const column = (title: string, key: string): TableHeaderColumn => ({
  title,
  key,
  type: 'string',
  sort: false,
})

const getRawColumns = (t: (key: string) => string): TableHeaderColumn[] => [
  column('', 'join'),
  column(t('common.type'), 'type'),
  column(t('common.date'), 'date'),
  column(t('common.country'), 'country'),
  column(t('common.vehicle'), 'vehicle'),
]

const getJoinedColumns = (t: (key: string) => string): TableHeaderColumn[] => [
  column(t('tmsDownload.docNumber'), 'docNumber'),
  column(t('tmsDownload.loadingDate'), 'loadingDate'),
  column(t('tmsDownload.loadingCountry'), 'loadingCountry'),
  column(t('tmsDownload.unloadingDate'), 'unloadingDate'),
  column(t('tmsDownload.unloadingCountry'), 'unloadingCountry'),
  column(t('tmsDownload.vehicle'), 'vehicle'),
  column(t('tmsDownload.routeType'), 'routeType'),
  column('', 'actions'),
]

const byDate =
  <T,>(getDate: (item: T) => Moment) =>
  (a: T, b: T) =>
    getDate(a).valueOf() - getDate(b).valueOf()

const routeTypeOf = (
  loadingCountry: CountryModel | undefined,
  unloadingCountry: CountryModel | undefined,
  loading: boolean,
) =>
  loadingCountry && unloadingCountry
    ? determineRouteType(loadingCountry, unloadingCountry, loading)
    : TmsRouteType.Undefined

const toEvent = (
  tripEvent: TripEventType,
  entry: JoinedEntry,
  date: Moment,
  country: CountryModel | undefined,
  secondDate: Moment,
  secondCountry: CountryModel | undefined,
): EventModel => ({
  lp: 0,
  tripEvent,
  date,
  country,
  docNumber: entry.docNumber || undefined,
  tmsRouteType: entry.routeType,
  secondDate,
  secondCountry,
  vehicleId: entry.vehicle?.id,
  registrationNumber: entry.vehicle?.registrationNumber,
})

// Odpowiednik WPF TachographTmsDataViewModel: rekordy załadunku/rozładunku z karty i z pojazdów,
// łączone ręcznie w pary, które trafiają do delegacji jako dokumenty TMS.
export const CardLoadsView: React.FC<CardLoadsViewProps> = ({
  companyId,
  driverId,
  driverName,
  dateFrom: initialDateFrom,
  dateTo: initialDateTo,
  existingEvents,
  onClose,
  onSave,
}) => {
  const { t } = useLocalize()
  const { getTachographLoadUnload } = useBusinessTripService()
  const { countries, countryOptions, lang } = useTmsCountryOptions(companyId)

  const [raw, setRaw] = React.useState<RawEntry[]>([])
  const [joined, setJoined] = React.useState<JoinedEntry[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [loadFailed, setLoadFailed] = React.useState(false)
  const [dateFrom, setDateFrom] = React.useState(
    initialDateFrom.format('YYYY-MM-DD'),
  )
  const [dateTo, setDateTo] = React.useState(initialDateTo.format('YYYY-MM-DD'))
  const [mode, setMode] = React.useState<TmsSourceMode>('driver')
  const [vehicleId, setVehicleId] = React.useState<string | null>(null)

  // WPF wyłącza „Połącz” dla rekordu, który jest już markerem delegacji (ta sama minuta i typ).
  const existingMarkers = React.useMemo(
    () =>
      new Set(
        existingEvents
          .filter(
            (e) =>
              e.date &&
              (e.tripEvent === TripEventType.Loading ||
                e.tripEvent === TripEventType.Unloading),
          )
          .map(
            (e) =>
              `${e.tripEvent}|${moment(e.date).format('YYYY-MM-DD HH:mm')}`,
          ),
      ),
    [existingEvents],
  )

  React.useEffect(() => {
    setRaw([])
    setJoined([])
    setLoadFailed(false)
    // W trybie „Pojazd” WPF nie ładuje niczego, dopóki pojazd nie jest wybrany.
    if (
      !driverId ||
      !dateFrom ||
      !dateTo ||
      (mode === 'vehicle' && !vehicleId)
    ) {
      setIsLoading(false)
      return undefined
    }

    let cancelled = false
    setIsLoading(true)
    getTachographLoadUnload({
      companyId,
      driverId,
      dateFrom,
      dateTo,
      vehicleId: mode === 'vehicle' ? vehicleId : null,
    })
      .then((records) => {
        if (cancelled) return
        setRaw(
          records.map((record) => {
            const tripEvent = record.isLoading
              ? TripEventType.Loading
              : TripEventType.Unloading
            return {
              ...record,
              key: uuidv4(),
              disabled: existingMarkers.has(
                `${tripEvent}|${record.date.format('YYYY-MM-DD HH:mm')}`,
              ),
            }
          }),
        )
      })
      .catch((err: any) => {
        if (cancelled) return
        setLoadFailed(true)
        toast.error({
          text:
            err?.response?.data?.message ||
            t('errorCodes.retrieving-card-loads-failed'),
        })
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, driverId, dateFrom, dateTo, mode, vehicleId])

  const moveToJoined = (
    loading: RawEntry,
    unloading: RawEntry,
    loadingClickedLast: boolean,
  ) => {
    const entry: JoinedEntry = {
      key: uuidv4(),
      docNumber: uuidv4(),
      loadingDate: loading.date,
      loadingCountry: loading.country,
      unloadingDate: unloading.date,
      unloadingCountry: unloading.country,
      vehicle: loading.vehicle,
      loading: loadingClickedLast,
      routeType: routeTypeOf(
        loading.country,
        unloading.country,
        loadingClickedLast,
      ),
    }
    setJoined((prev) => [...prev, entry].sort(byDate((j) => j.loadingDate)))
    setRaw((prev) => prev.filter((r) => r !== loading && r !== unloading))
  }

  const handleJoin = (item: RawEntry) => {
    const target = raw.find((r) => r.key === item.key)!

    if (target.checked) {
      setRaw(raw.map((r) => (r === target ? { ...r, checked: false } : r)))
      return
    }

    const checked = raw.filter((r) => r.checked)
    const warn = (key: string) => toast.warning({ text: t(key) })

    if (checked.length === 2) {
      warn('businessTrip.card-loads-cannot-join-more-than-two')
    } else if (checked.length === 0) {
      setRaw(raw.map((r) => (r === target ? { ...r, checked: true } : r)))
    } else if (checked.some((r) => r.isLoading) && !target.isLoading) {
      if (checked[0].date.isAfter(target.date)) {
        warn('businessTrip.card-loads-unloading-before-loading')
        return
      }
      moveToJoined(checked[0], target, false)
    } else if (checked.some((r) => !r.isLoading) && target.isLoading) {
      if (checked[0].date.isBefore(target.date)) {
        warn('businessTrip.card-loads-unloading-before-loading')
        return
      }
      moveToJoined(target, checked[0], true)
    } else {
      warn(
        target.isLoading
          ? 'businessTrip.card-loads-cannot-join-two-loadings'
          : 'businessTrip.card-loads-cannot-join-two-unloadings',
      )
    }
  }

  const handleRemove = (entry: JoinedEntry) => {
    const restored: RawEntry[] = [
      {
        key: uuidv4(),
        date: entry.loadingDate,
        isLoading: true,
        country: entry.loadingCountry,
        vehicle: entry.vehicle,
        disabled: false,
      },
      {
        key: uuidv4(),
        date: entry.unloadingDate,
        isLoading: false,
        country: entry.unloadingCountry,
        vehicle: entry.vehicle,
        disabled: false,
      },
    ]
    setRaw((prev) => [...prev, ...restored].sort(byDate((r) => r.date)))
    setJoined((prev) => prev.filter((j) => j !== entry))
  }

  const updateJoined = (entry: JoinedEntry, patch: Partial<JoinedEntry>) => {
    setJoined((prev) =>
      prev.map((j) => {
        if (j !== entry) return j
        const next = { ...j, ...patch }
        return {
          ...next,
          routeType: routeTypeOf(
            next.loadingCountry,
            next.unloadingCountry,
            next.loading,
          ),
        }
      }),
    )
  }

  const handleCountryChange = (
    entry: JoinedEntry,
    field: 'loadingCountry' | 'unloadingCountry',
    name: string,
  ) => {
    const country = countries.find((c) => c.name === name)
    if (country) updateJoined(entry, { [field]: country })
  }

  const handleSave = () => {
    if (joined.length === 0) {
      onClose()
      return
    }

    const events = [...joined]
      .sort(
        (a, b) =>
          a.loadingDate.valueOf() - b.loadingDate.valueOf() ||
          a.unloadingDate.valueOf() - b.unloadingDate.valueOf(),
      )
      .flatMap((entry) => [
        toEvent(
          TripEventType.Loading,
          entry,
          entry.loadingDate,
          entry.loadingCountry,
          entry.unloadingDate,
          entry.unloadingCountry,
        ),
        toEvent(
          TripEventType.Unloading,
          entry,
          entry.unloadingDate,
          entry.unloadingCountry,
          entry.loadingDate,
          entry.loadingCountry,
        ),
      ])

    onSave(events)
  }

  const countryLabel = (country?: CountryModel) =>
    country ? translateCountryName(country.name, lang) : '-'

  const section = (
    title: string,
    count: number,
    className: string,
    table: React.ReactNode,
  ) => (
    <div className={`tw-min-w-0 ${className}`}>
      <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2">
        <h3 className="tw-text-sm tw-font-semibold tw-text-content-base">
          {title}
        </h3>
        <span className="tw-rounded-full tw-bg-secondary tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-primary">
          {count}
        </span>
      </div>
      <div className="tw-overflow-x-auto tw-overflow-y-hidden tw-rounded-lg tw-border tw-border-base-300">
        <table className="tw-w-full tw-border-collapse">{table}</table>
      </div>
    </div>
  )

  const hasData = raw.length > 0 || joined.length > 0

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-white">
      <TmsImportHeader
        onBack={onClose}
        translate={t}
        title={t('businessTrip.tms-card-loads')}
      />

      <TmsSourceFilter
        companyId={companyId}
        name="card-loads-source"
        driverName={driverName}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        mode={mode}
        onModeChange={setMode}
        vehicleId={vehicleId}
        onVehicleChange={setVehicleId}
      />

      <div className="tw-flex-1 tw-overflow-auto tw-bg-white tw-px-6 tw-py-4">
        {isLoading ? (
          <Loader
            className="tw-py-12"
            text={t('businessTrip.card-loads-geocoding')}
          />
        ) : !hasData ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span className={loadFailed ? 'tw-text-error' : ''}>
              {t(
                loadFailed
                  ? 'errorCodes.retrieving-card-loads-failed'
                  : 'businessTrip.card-loads-no-data',
              )}
            </span>
          </div>
        ) : (
          // Obok siebie jak w WPF; gdy tabela par się nie mieści (edycja delegacji), idzie nad listę.
          <div className="tw-flex tw-flex-wrap-reverse tw-gap-6">
            {section(
              t('businessTrip.card-loads-raw'),
              raw.length,
              'tw-flex-[1_1_480px]',
              <>
                <TableHeader
                  columns={getRawColumns(t)}
                  disabledCheckboxToggle
                  sortConfig={null}
                />
                <tbody>
                  {raw.map((entry) => {
                    const tripEvent = entry.isLoading
                      ? TripEventType.Loading
                      : TripEventType.Unloading
                    return (
                      <tr
                        key={entry.key}
                        className={`tw-border-b tw-border-base-200 ${entry.checked ? 'tw-bg-secondary' : 'hover:tw-bg-base-100'}`}
                      >
                        <td className="tw-px-2 tw-py-2">
                          <Button
                            size="xs"
                            variant="primary"
                            mode={entry.checked ? 'fill' : 'outline'}
                            text={t('businessTrip.card-loads-join')}
                            className={entry.disabled ? 'tw-invisible' : ''}
                            onClick={() => handleJoin(entry)}
                          />
                        </td>
                        <TableTextRow
                          size="compact"
                          pointer={false}
                          icon={<FaIcon icon={getEventTypeIcon(tripEvent)} />}
                        >
                          {t(
                            entry.isLoading
                              ? 'tripEventType.Loading'
                              : 'tripEventType.Unloading',
                          )}
                        </TableTextRow>
                        <TableTextRow size="compact" pointer={false}>
                          {entry.date.format('YYYY-MM-DD HH:mm')}
                        </TableTextRow>
                        <TableTextRow size="compact" pointer={false}>
                          {countryLabel(entry.country)}
                        </TableTextRow>
                        <TableTextRow size="compact" pointer={false}>
                          {entry.vehicle?.registrationNumber || '-'}
                        </TableTextRow>
                      </tr>
                    )
                  })}
                </tbody>
              </>,
            )}

            {section(
              t('businessTrip.card-loads-connected'),
              joined.length,
              'tw-flex-[3_1_1080px]',
              <>
                <TableHeader
                  columns={getJoinedColumns(t)}
                  disabledCheckboxToggle
                  sortConfig={null}
                />
                <tbody>
                  {joined.map((entry) => (
                    <tr
                      key={entry.key}
                      className="tw-border-b tw-border-white"
                      style={{
                        backgroundColor: getRowBackground(entry.routeType),
                      }}
                    >
                      <td className="tw-px-2 tw-py-2">
                        <Input
                          type="text"
                          value={entry.docNumber}
                          onValueChange={(value) =>
                            updateJoined(entry, { docNumber: value })
                          }
                          className="tw-w-full tw-min-w-[110px] tw-rounded tw-border tw-border-base-300 tw-bg-white tw-px-2 tw-py-1 tw-text-sm tw-text-content-base focus:tw-border-primary focus:tw-outline-none"
                        />
                      </td>
                      <td className="tw-px-2 tw-py-2">
                        <TmsDateTimeInput
                          value={entry.loadingDate}
                          onChange={(date) =>
                            updateJoined(entry, { loadingDate: date })
                          }
                        />
                      </td>
                      <td className="tw-min-w-[150px] tw-px-2 tw-py-2">
                        <Dropdown
                          compact
                          searchable
                          portalMenu
                          options={countryOptions}
                          value={entry.loadingCountry?.name ?? null}
                          placeholder={countryLabel(entry.loadingCountry)}
                          searchPlaceholder={t('menu.search')}
                          triggerClassName="tw-bg-white"
                          onChange={(name) =>
                            handleCountryChange(entry, 'loadingCountry', name)
                          }
                        />
                      </td>
                      <td className="tw-px-2 tw-py-2">
                        <TmsDateTimeInput
                          value={entry.unloadingDate}
                          onChange={(date) =>
                            updateJoined(entry, { unloadingDate: date })
                          }
                        />
                      </td>
                      <td className="tw-min-w-[150px] tw-px-2 tw-py-2">
                        <Dropdown
                          compact
                          searchable
                          portalMenu
                          options={countryOptions}
                          value={entry.unloadingCountry?.name ?? null}
                          placeholder={countryLabel(entry.unloadingCountry)}
                          searchPlaceholder={t('menu.search')}
                          triggerClassName="tw-bg-white"
                          onChange={(name) =>
                            handleCountryChange(entry, 'unloadingCountry', name)
                          }
                        />
                      </td>
                      <TableTextRow size="compact" pointer={false}>
                        {entry.vehicle?.registrationNumber || '-'}
                      </TableTextRow>
                      <TableTextRow size="compact" pointer={false}>
                        {t(getTmsRouteTypeName(entry.routeType)) || '-'}
                      </TableTextRow>
                      <td className="tw-px-2 tw-py-2">
                        <button
                          type="button"
                          title={t('tmsDownload.remove-row')}
                          onClick={() => handleRemove(entry)}
                          className="tw-rounded tw-p-1.5 tw-text-content-base hover:tw-bg-white/60"
                        >
                          <FaIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>,
            )}
          </div>
        )}
      </div>

      <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-px-6 tw-py-4">
        <Button
          size="sm"
          variant="neutral"
          mode="outline"
          text={t('answer.cancel')}
          onClick={onClose}
        />
        <Button
          size="sm"
          variant="primary"
          text={t('menu.save')}
          disabled={isLoading}
          onClick={handleSave}
        />
      </div>
    </div>
  )
}
